import React, { useMemo, useRef } from 'react';
import { Gesture } from 'react-native-gesture-handler';
import type { Annotation, AnnotationPoint, GestureLikeEvent } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import type { ImageViewTransform } from '@v2/components/imageHandling/components/DrawingCanvas/helpers/imageViewTransformStore';
import { hitTest } from '@v2/components/imageHandling/components/DrawingCanvas/helpers';

export interface UseManageAnnotationLayerParams {
  isAnnotating: boolean;
  viewTransform: ImageViewTransform;
  annotations: Annotation[];
  hitThreshold: number;
  onPointerDown?: (point: AnnotationPoint, e: GestureLikeEvent) => void;
  onPointerMove?: (point: AnnotationPoint, e: GestureLikeEvent) => void;
  onPointerUp?: (e: { pointerId?: number }) => void;
  onDoubleClick?: (point: AnnotationPoint, e: { clientX: number; clientY: number }) => void;
  /** Optional ref shared with the controller so its non-passive wheel listener attaches to this div. */
  webContainerRefExternal?: React.MutableRefObject<HTMLDivElement | null>;
}

/**
 * All pointer/gesture wiring for AnnotationLayer. Web exposes DOM event handlers + a shared ref;
 * native exposes the composed gesture (pan + double-tap). Component stays presentational.
 */
export const useManageAnnotationLayer = ({
  isAnnotating,
  viewTransform,
  annotations,
  hitThreshold,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onDoubleClick,
  webContainerRefExternal,
}: UseManageAnnotationLayerParams) => {
  const webContainerRef = useRef<HTMLDivElement | null>(null);

  // While annotating, the layer owns ALL pointer events. Hit-testing happens in JS — shape hits
  // route to selection/draft creation; empty-space hits forward to image pan/zoom via the controller.
  const containerPointerEvents: 'auto' | 'none' = isAnnotating ? 'auto' : 'none';

  // Convert client coords to the SVG's viewBox coord system, undoing the CSS transform (translate
  // pan, scale zoom) applied around the container center.
  const pointToContainer = (clientX: number, clientY: number): AnnotationPoint | null => {
    const rect = webContainerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const { zoom, panX, panY } = viewTransform;
    const z = zoom || 1;
    return {
      x: (relX - cx - panX) / z + cx,
      y: (relY - cy - panY) / z + cy,
    };
  };

  const setWebContainerRef = (node: HTMLDivElement | null) => {
    webContainerRef.current = node;
    if (webContainerRefExternal) webContainerRefExternal.current = node;
  };

  const handleWebPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = pointToContainer(e.clientX, e.clientY);
    if (!p) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Some browsers throw if already captured elsewhere — ignore.
    }
    onPointerDown?.(p, {
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      clientX: e.clientX,
      clientY: e.clientY,
      pointerId: e.pointerId,
    });
  };

  const handleWebPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = pointToContainer(e.clientX, e.clientY);
    if (!p) return;
    onPointerMove?.(p, {
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      clientX: e.clientX,
      clientY: e.clientY,
      pointerId: e.pointerId,
    });
  };

  const handleWebPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Ignore release on a non-captured pointer.
    }
    onPointerUp?.({ pointerId: e.pointerId });
  };

  const handleWebPointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
    onPointerUp?.({ pointerId: e.pointerId });
  };

  const isPointOnAnnotation = (p: AnnotationPoint) =>
    annotations.some((annotation) => hitTest({ annotation, pointerPosition: p, threshold: hitThreshold }));

  const handleWebDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const p = pointToContainer(e.clientX, e.clientY);
    if (!p) return;
    if (isPointOnAnnotation(p)) {
      e.preventDefault();
      e.stopPropagation();
    }
    onDoubleClick?.(p, { clientX: e.clientX, clientY: e.clientY });
  };

  const nativeGesture = useMemo(() => {
    // `.runOnJS(true)` keeps callbacks on the JS thread so plain (non-worklet) JS handlers can be
    // invoked safely. Without it, gesture-handler v2 runs callbacks on the UI thread and throws
    // "Tried to synchronously call non-worklet anonymous function on the UI thread".
    // `clientX/Y` semantics on native = coords local to this layer (matches the web pipeline
    // where they're used as drawing-surface-local). Using `absoluteX/Y` would put the
    // TextInput overlay way off-screen because the overlay is positioned inside this layer.
    const pan = Gesture.Pan()
      .runOnJS(true)
      .onBegin((e) => {
        onPointerDown?.({ x: e.x, y: e.y }, { clientX: e.x, clientY: e.y });
      })
      .onUpdate((e) => {
        onPointerMove?.({ x: e.x, y: e.y }, { clientX: e.x, clientY: e.y });
      })
      .onFinalize(() => {
        onPointerUp?.({});
      });
    const doubleTap = Gesture.Tap()
      .runOnJS(true)
      .numberOfTaps(2)
      .onEnd((e) => {
        onDoubleClick?.({ x: e.x, y: e.y }, { clientX: e.x, clientY: e.y });
      });
    return Gesture.Race(doubleTap, pan);
  }, [onDoubleClick, onPointerDown, onPointerMove, onPointerUp]);

  return {
    containerPointerEvents,
    setWebContainerRef,
    handleWebPointerDown,
    handleWebPointerMove,
    handleWebPointerUp,
    handleWebPointerLeave,
    handleWebDoubleClick,
    nativeGesture,
  };
};
